import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Groupper, Header, Icon, Input, Pagination, Table } from 'react-frontier';
import { Dropdown } from 'semantic-ui-react';
import { Shipment } from '../SiteClasses';
import moment from 'moment';
import API from '../API';
import classNames from 'classnames';

const PAGE_SIZE = 20;

enum StatusFilter{
	PENDING = 0,
	SHIPPED = 1,
	DELIVERED = 2,
	PERMANENT = 3,
}

var ShipmentList = ()=>{
	var [shipments, setShipments] = useState<Shipment[]>(null);
	var [loadError, setLoadError] = useState<string>(null);
	var [loading, setLoading] = useState<boolean>(false);
	var [search, setSearch] = useState<string>('');
	var [searchTimeout, setSearchTimeout] = useState<any>(null);
	var [page, setPage] = useState<number>(0);
	var [statusFilters, setStatusFilters] = useState<StatusFilter[]>([]);
	var [hasNext, setHasNext] = useState<boolean>(false);

	useEffect(()=>{
		get();
	}, []);

	useEffect(()=>{
		if(shipments !== null){
			setPage(0);
			get(0, search.length >= 2 ? search : null);
		}
	}, [statusFilters]);

	var get = (get_page?: number, query?: string)=>{
		var p = get_page ?? page;
		var offset = p * PAGE_SIZE;
		setLoading(true);
		API.getShipments(offset, PAGE_SIZE + 1, query, statusFilters).then(res=>{
			if(res.error) return setLoadError(res.message);
			setHasNext(res.data.length > PAGE_SIZE);
			setShipments(res.data.slice(0, PAGE_SIZE));
		}).catch(()=>{
			setLoadError('Hubo un error inesperado cargando los envíos.');
		}).finally(()=>{
			setLoading(false);
		});
	};

	var onSearchChange = (val: string)=>{
		setSearch(val);
		setPage(0);
		if(searchTimeout) clearTimeout(searchTimeout);
		if(val.length > 0 && val.length < 2) return;
		setSearchTimeout(setTimeout(()=>{
			get(0, val.length >= 2 ? val : null);
		}, 500));
	};

	var onPageChange = (p: number)=>{
		setPage(p);
		get(p, search.length >= 2 ? search : null);
	};

	var onStatusFilter = (s: StatusFilter)=>{
		setStatusFilters(prev=>{
			if(prev.includes(s)) return prev.filter(x=>x !== s);
			if(prev.length >= 4) return prev;
			return [...prev, s];
		});
	};

	if(loadError) return <Header text='Error' subtext={loadError} />;

	return <Groupper width={1150} title='Envíos' fitted style={{ marginTop: 20 }} titleRight={(
		<Button text='Agregar' iconName='circle-plus' color='black' size='small' as={Link} to='/shipments/create' />
	)}>
		<div style={{ display: 'flex', gap: 10, marginBottom: 10, padding: '10px 10px 0' }}>
			<Input placeholder='Buscar por creador, evento o ciudad destino...' value={search} onChange={onSearchChange} style={{ flex: 1 }} />
			<Dropdown icon={null} pointing direction='left' trigger={(
				<Button
					text='Filtros'
					iconRight={statusFilters.length > 0 ? 'asterisk' : null}
					iconRightStyle={{ color: 'brown', marginLeft: 5 }}
					size='small'
					iconName='filter'
				/>
			)}>
				<Dropdown.Menu>
					<Dropdown.Item text='Limpiar filtros' icon='trash' onClick={()=>setStatusFilters([])} />
					<Dropdown.Divider />
					<Dropdown.Header content='Estatus' />
					{([
						[StatusFilter.PENDING, 'Pendiente'],
						[StatusFilter.SHIPPED, 'Enviado'],
						[StatusFilter.DELIVERED, 'Entregado'],
						[StatusFilter.PERMANENT, 'Permanente'],
					] as [StatusFilter, string][]).map(([s, label])=>(
						<Dropdown.Item key={s} onClick={()=>onStatusFilter(s)}>
							<i className={classNames('icon', {
								'check square': statusFilters.includes(s),
								'square outline': !statusFilters.includes(s),
							})}></i>
							{label}
						</Dropdown.Item>
					))}
				</Dropdown.Menu>
			</Dropdown>
		</div>
		<Table
			striped
			headers={['ID', 'Origen', "Descripción", 'Destino', 'Equipos', 'Creador', 'Estado', 'Retorno', 'Fecha', '']}
			collapsingIndexes={[0, 3, 5, 6, 7, 8]}
			centeredIndexes={[5, 6]}
			footer={(shipments && (hasNext || page !== 0)) && (
				<Table.Cell row colSpan={10}>
					<div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
						<Pagination page={page} onPageChange={onPageChange} hasNext={hasNext} />
					</div>
				</Table.Cell>
			)}
		>
			{loading || !shipments ? (
				<Table.Cell row colSpan={10}>
					<Header loading text='Cargando' />
				</Table.Cell>
			) : shipments.length === 0 ? (
				<Table.Cell row className='empty' colSpan={10}>
					No se encontraron registros
				</Table.Cell>
			) : (
				shipments.map(s=>{
					var isFinalized = (s.item_count || 0) === 0 || (!!s.warehouse_id && !!s.delivered);
					var status = s.deleted
						? { label: 'Cancelado', color: 'red' }
						: isFinalized
						? { label: 'Finalizado', color: 'black' }
						: s.delivered
						? { label: 'Entregado', color: 'green' }
						: s.shipped
						? { label: 'Enviado', color: 'blue' }
						: { label: 'Pendiente', color: null };
					var retorno: React.ReactNode = <Icon name='minus' />;
					if(isFinalized){
						retorno = <div className='fr label black'>OK</div>;
					}else if(s.date_return){
						var now = moment().unix();
						var diff = s.date_return - now;
						if(diff <= 0){
							var overdueDays = Math.ceil(diff / 86400);
							retorno = <div className='fr label red'>{overdueDays}</div>;
						}else{
							var days = Math.ceil(diff / 86400);
							retorno = <div className={classNames('fr label', { 'red': days < 3, 'yellow': days >= 3 && days <= 7, 'green': days > 7 })}>{days}</div>;
						}
					}
					return (
						<Table.Row
							key={s.shipment_id}
							collapsingIndexes={[0, 3, 5, 6, 7, 8]}
							centeredIndexes={[5, 6]}
							data={[
								s.shipment_id,
								s.warehouse_name || (s.request_id ? `Solicitud #${s.request_id}` : <Icon name='minus' />),
								s.description || <Icon name='minus' />,
								s.destination_city || <Icon name='minus' />,
								s.item_count || 0,
								s.user_creator_name,
								<div className={classNames('fr label', status.color)}>{status.label}</div>,
								retorno,
								moment.unix(s.date_created).format('DD/MMM/YY'),
								<Dropdown icon={(
									<div className='fr label' style={{ padding: '3px 10px' }}>
										<Icon name='ellipsis' style={{ margin: 0 }} />
									</div>
								)} className='icon'>
									<Dropdown.Menu>
										<Dropdown.Item as={Link} to={`/shipments/${s.shipment_id}`}>
											<Icon name='eye' style={{ marginRight: 3 }} /> Ver
										</Dropdown.Item>
									</Dropdown.Menu>
								</Dropdown>,
							]}
						/>
					);
				})
			)}
		</Table>
	</Groupper>;
}

export default ShipmentList;
